import { useState, useEffect, useCallback, useRef } from "react";
import { can, ROLES, UC, UC2 } from "./config";
import { apiCall } from "./api";
import { RoleBadge } from "./components/common/Badge";
import LoginScreen from "./components/LoginScreen";
import UnitSwitcher from "./components/UnitSwitcher";
import DraftsPage from "./components/DraftsPage";
import Dashboard from "./components/Dashboard";
import MachineListPage from "./components/MachineList";
import InstrumentListPage from "./components/InstrumentList";
import CalibrationPlan from "./components/CalibrationPlan";
import PMCheckSheetsAdmin from "./components/PMCheckSheetsAdmin";
import HistoryCard from "./components/HistoryCard";
import PMPlan from "./components/PMPlan";
import SpareParts from "./components/SpareParts";
import Checksheets from "./components/Checksheets";
import Settings from "./components/Settings";
import Reports from "./components/Reports";
import MachineScan from "./components/MachineScan";
import { refreshRolesCache, clearRolesCache } from "./rolesCache";
import { refreshMaintenanceTypes, resetMaintenanceTypes } from "./maintenanceTypesCache";
import "./App.css";

// Handle /scan/:unitId/:machineId routes (public, no auth)
function getScanParams() {
  const match = window.location.pathname.match(/^\/scan\/([^/]+)\/([^/]+)/);
  return match ? { unitId: match[1], machineId: match[2] } : null;
}

// The sidebar had ten flat entries with no indication that four of them are about the
// machines and two about the instruments. They are grouped now: a group is a heading that
// opens, never a destination of its own, so clicking it can only ever reveal — it cannot
// take you somewhere you did not ask for.
const NAV = [
  { id: "dashboard", label: "Dashboard" },
  { id: "grp-machines", label: "Machine List", children: [
    { id: "machines", label: "Machine List" },
    { id: "pm", label: "PM Plan" },
    { id: "spare", label: "Spare Parts" },
    { id: "checksheet", label: "Checksheets" },
    // Its own section rather than a tab inside Settings, so the standards room can be
    // given the templates without being given users, roles and backups along with them.
    { id: "pmchecksheets", label: "PM Check Sheets", perm: "managePMCheckSheetTemplate" },
  ]},
  { id: "grp-instruments", label: "Instrument List", children: [
    { id: "instruments", label: "Instrument List" },
    { id: "calibration", label: "Calibration Plan" },
  ]},
  { id: "drafts", label: "Drafts" },
  { id: "reports", label: "Reports", perm: "accessReports" },
  { id: "settings", label: "Settings", perm: "accessSettings" },
];

// Which group a view sits in, so the right one is already open when a view is reached
// from somewhere other than the sidebar — a QR scan, the Back button, a draft.
const GROUP_OF_VIEW = {};
for (const entry of NAV) {
  for (const child of entry.children || []) GROUP_OF_VIEW[child.id] = entry.id;
}
GROUP_OF_VIEW.history = "grp-machines";   // a machine's History Card belongs to the list

export const BLANK_MACHINE_FILTERS = { tab: "machines", search: "", dept: "ALL", type: "ALL", sortBy: "default", schedule: "ALL" };

// Full-screen loader used at app boot AND during unit switch, so the
// dashboard never renders with stale (previous unit's) data.
function FullScreenLoader({ text }) {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="fullscreen-loader">
      <div className="text-center">
        <img
          src="/logo.jpg"
          alt="Advanced Anmol Metcomp Pvt Ltd"
          className="w-16 h-16 mx-auto mb-3 rounded-xl object-contain animate-pulse"
        />
        <p className="text-gray-500">{text || "Loading..."}</p>
      </div>
    </div>
  );
}

export default function App() {
  const scanParams = getScanParams();
  if (scanParams) return <MachineScan unitId={scanParams.unitId} machineId={scanParams.machineId} />;
  return <MainApp />;
}

function MainApp() {
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [machines, setMachines] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [pmPlan, setPmPlan] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [checksheets, setChecksheets] = useState([]);
  const [pmCheckSheetTemplates, setPmCheckSheetTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [floors, setFloors] = useState([]);

  const [currentUser, setCurrentUser] = useState(null);
  // Hydrate from localStorage so the picker remembers the last unit across reloads.
  // Non-superadmins get overwritten on verify/login with their own user.unitId.
  const [currentUnitId, setCurrentUnitId] = useState(
    () => localStorage.getItem("lastUnitId") || "u1"
  );
  const [view, setView] = useState("dashboard");
  const [selectedMachine, setSelectedMachine] = useState(null);
  // Machine List filters live up here so they survive opening a machine's History Card
  // and coming back — the list itself unmounts while the History Card is on screen,
  // which used to throw away the search box and department filter.
  const [machineFilters, setMachineFilters] = useState(BLANK_MACHINE_FILTERS);
  // A PM Health row says what is wrong but not where to put it right. Clicking one opens
  // the screen that owns the fix, already searched down to the machine in question: the
  // Machine List for a machine to edit, delete or schedule, the PM Plan for a stray row.
  const [pmSearchSeed, setPmSearchSeed] = useState("");
  const [highlightLogId, setHighlightLogId] = useState(null);
  // Set when a machine is opened from the Drafts section, so the History Card opens the
  // draft straight away instead of landing on the machine's history.
  const [resumeDraftMachineId, setResumeDraftMachineId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Groups the user has opened or closed by hand. A group holding the current view is
  // open regardless, so you can always see where you are.
  const [openGroups, setOpenGroups] = useState({});
  const [loading, setLoading] = useState(true);
  // NEW: true only after fetchData has populated state for the *current* unit.
  // Gates rendering of the main app body so stale arrays from the previous
  // unit are never visible on the dashboard.
  const [unitDataReady, setUnitDataReady] = useState(false);

  // BUGFIX 2026-05-16: race-guard for fetchData.
  const latestUnitRef = useRef(currentUnitId);
  useEffect(() => { latestUnitRef.current = currentUnitId; }, [currentUnitId]);

  // ---- Browser / device Back button -------------------------------------
  // The app has no router, so it used to push nothing onto the browser history.
  // Pressing Back on a machine's History Card left the site altogether instead of
  // returning to the list you came from. Every in-app navigation now pushes an
  // entry, and popstate puts the matching view back on screen.
  const machinesRef = useRef(machines);
  useEffect(() => { machinesRef.current = machines; }, [machines]);

  const navigate = useCallback((nextView, machine = null, { replace = false } = {}) => {
    setView(nextView);
    setSelectedMachine(machine);
    const entry = { appView: { view: nextView, machineId: machine?.id || null } };
    try {
      if (replace) window.history.replaceState(entry, "");
      else window.history.pushState(entry, "");
    } catch { /* history unavailable — navigation still works, Back just won't */ }
  }, []);

  const fixPMHealth = useCallback((where, row) => {
    const term = (row.machineCode || "").trim() || (row.name || "").trim();
    if (where === "machines") {
      setMachineFilters({ ...BLANK_MACHINE_FILTERS, search: term });
      setPmSearchSeed("");
      navigate("machines");
    } else {
      setPmSearchSeed(term);
      navigate("pm");
    }
  }, [navigate]);

  // Picking a screen from the menu is a fresh start, so it drops any search a PM Health
  // row seeded — otherwise the plan would still look half empty hours later.
  const navFromMenu = useCallback(id => { setPmSearchSeed(""); navigate(id); }, [navigate]);

  useEffect(() => {
    function onPop(e) {
      const s = e.state?.appView;
      if (!s) return;   // an entry we didn't create — let the browser have it
      const machine = s.machineId
        ? (machinesRef.current || []).find(m => m.id === s.machineId) || null
        : null;
      // The machine can be gone (deleted, or a different unit is loaded). Fall back
      // to the list rather than rendering a blank History Card.
      setView(s.view === "history" && !machine ? "machines" : (s.view || "dashboard"));
      setSelectedMachine(machine);
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Seed the first entry so the dashboard is a real history position to come back to.
  useEffect(() => {
    if (!currentUser) return;
    try {
      window.history.replaceState({ appView: { view: "dashboard", machineId: null } }, "");
    } catch { /* ignore */ }
  }, [currentUser]);

  // Force the page title — emergent-main.js overwrites <title> after load.
  useEffect(() => { document.title = "Maintenance AAMPL"; }, []);

  useEffect(() => {
    const safetyTimer = setTimeout(() => setLoading(false), 25000);
    const token = localStorage.getItem("token");
    if (token) {
      verifyToken().finally(() => clearTimeout(safetyTimer));
    } else {
      setLoading(false);
      clearTimeout(safetyTimer);
    }
    return () => clearTimeout(safetyTimer);
  }, []);

  async function verifyToken() {
    try {
      const response = await apiCall("GET", "/auth/verify");
      setCurrentUser(response.user);
      if (response.user.role !== "superadmin") {
        setCurrentUnitId(response.user.unitId || "u1");
      } else {
        const remembered = localStorage.getItem("lastUnitId");
        if (!remembered) setCurrentUnitId(response.user.unitId || "u1");
      }
      refreshRolesCache();
      refreshMaintenanceTypes();
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
    }
    finally { setLoading(false); }
  }

  const fetchData = useCallback(async () => {
    if (!currentUser) return;
    const myUnitId = currentUnitId;
    try {
      const [unitsData, machinesData, pmData, sparesData, historyData, checksheetsData, deptsData, floorsData, pmCheckSheetTemplatesData, instrumentsData] = await Promise.all([
        apiCall("GET", "/units"),
        apiCall("GET", `/machines/${myUnitId}`),
        apiCall("GET", `/pm-plans/${myUnitId}`),
        apiCall("GET", `/spare-parts/${myUnitId}`),
        apiCall("GET", `/history/${myUnitId}`),
        apiCall("GET", "/checksheets"),
        apiCall("GET", `/departments/${myUnitId}`),
        apiCall("GET", `/floors/${myUnitId}`),
        apiCall("GET", "/pm-check-sheet-templates"),
        // Instruments are unit-scoped like machines. Failing softly keeps an older
        // backend (no /instruments route yet) from taking the whole unit load down.
        apiCall("GET", `/instruments/${myUnitId}`).catch(() => []),
      ]);
      if (myUnitId !== latestUnitRef.current) {
        console.warn(`Discarding stale fetch for unit ${myUnitId} (current: ${latestUnitRef.current})`);
        return;
      }
      setUnits(unitsData); setMachines(machinesData); setPmPlan(pmData);
      setSpareParts(sparesData); setHistoryLogs(historyData); setChecksheets(checksheetsData); setDepartments(deptsData); setFloors(floorsData);
      setPmCheckSheetTemplates(pmCheckSheetTemplatesData);
      setInstruments(instrumentsData || []);
      setUnitDataReady(true);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      if (myUnitId === latestUnitRef.current) setUnitDataReady(true);
    }
  }, [currentUser, currentUnitId]);

  const fetchUsers = useCallback(async () => {
    if (!currentUser || !can(currentUser, "manageUsers")) return;
    try { const usersData = await apiCall("GET", "/users"); setUsers(usersData); }
    catch (error) { console.error("Failed to fetch users:", error); }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) { fetchData(); fetchUsers(); }
  }, [currentUser, currentUnitId, fetchData, fetchUsers]);

  function handleLogin(user) {
    setCurrentUser(user);
    if (user.role !== "superadmin") {
      setCurrentUnitId(user.unitId || "u1");
    } else {
      const remembered = localStorage.getItem("lastUnitId");
      if (!remembered) setCurrentUnitId(user.unitId || "u1");
    }
    refreshRolesCache();
    refreshMaintenanceTypes();
  }

  function handleLogout() {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    // Intentionally keep "lastUnitId" so next login lands on the same unit.
    clearRolesCache();
    resetMaintenanceTypes();
    setCurrentUser(null);
    setMachineFilters(BLANK_MACHINE_FILTERS);
    navigate("dashboard", null, { replace: true });
    setUnitDataReady(false);
  }

  function switchUnit(uid) {
    if (uid === currentUnitId) return;
    // CLEAR unit-scoped state IMMEDIATELY so the dashboard cannot render
    // with the previous unit's machine/PM/spares arrays during the fetch.
    setMachines([]);
    setInstruments([]);
    setPmPlan([]);
    setSpareParts([]);
    setHistoryLogs([]);
    setDepartments([]);
    setUnitDataReady(false);
    setCurrentUnitId(uid);
    // Filters belong to the unit you were looking at, so start the new one clean.
    setMachineFilters(BLANK_MACHINE_FILTERS);
    // Replace rather than push: Back should not drop you into the other unit's view.
    navigate("dashboard", null, { replace: true });
    localStorage.setItem("lastUnitId", uid);
  }

  const refreshMachines = useCallback(async () => {
    const myUnitId = currentUnitId;
    const data = await apiCall("GET", `/machines/${myUnitId}`);
    if (myUnitId === latestUnitRef.current) setMachines(data);
  }, [currentUnitId]);
  const refreshInstruments = useCallback(async () => {
    const myUnitId = currentUnitId;
    const data = await apiCall("GET", `/instruments/${myUnitId}`);
    if (myUnitId === latestUnitRef.current) setInstruments(data);
  }, [currentUnitId]);
  const refreshPM = useCallback(async () => {
    const myUnitId = currentUnitId;
    const data = await apiCall("GET", `/pm-plans/${myUnitId}`);
    if (myUnitId === latestUnitRef.current) setPmPlan(data);
  }, [currentUnitId]);
  const refreshSpares = useCallback(async () => {
    const myUnitId = currentUnitId;
    const data = await apiCall("GET", `/spare-parts/${myUnitId}`);
    if (myUnitId === latestUnitRef.current) setSpareParts(data);
  }, [currentUnitId]);
  const refreshHistory = useCallback(async () => {
    const myUnitId = currentUnitId;
    const data = await apiCall("GET", `/history/${myUnitId}`);
    if (myUnitId === latestUnitRef.current) setHistoryLogs(data);
  }, [currentUnitId]);
  const refreshUnits = useCallback(async () => { const data = await apiCall("GET", "/units"); setUnits(data); }, []);
  const refreshDepartments = useCallback(async () => {
    const myUnitId = currentUnitId;
    const data = await apiCall("GET", `/departments/${myUnitId}`);
    if (myUnitId === latestUnitRef.current) setDepartments(data);
  }, [currentUnitId]);

  const refreshFloors = useCallback(async () => {
    const myUnitId = currentUnitId;
    const data = await apiCall("GET", `/floors/${myUnitId}`);
    if (myUnitId === latestUnitRef.current) setFloors(data);
  }, [currentUnitId]);

  // M31: After units load, validate the stored lastUnitId still exists.
  // Guards against a stale localStorage value when a unit has been deleted.
  useEffect(() => {
    if (!currentUser || units.length === 0) return;
    if (!units.some(u => u.id === currentUnitId)) {
      const fallbackId = units[0].id;
      localStorage.setItem("lastUnitId", fallbackId);
      setCurrentUnitId(fallbackId);
    }
  }, [units, currentUser, currentUnitId]);

  if (loading) return <FullScreenLoader text="Loading..." />;
  if (!currentUser) return <LoginScreen onLogin={handleLogin} />;

  const unit = units.find(u => u.id === currentUnitId);
  const loaderUnitName = unit?.shortName || currentUnitId;

  if (!unitDataReady) return <FullScreenLoader text={`Loading ${loaderUnitName}...`} />;

  const allowed = n => !n.perm || can(currentUser, n.perm);
  const visibleNav = NAV
    .filter(allowed)
    .map(n => (n.children ? { ...n, children: n.children.filter(allowed) } : n))
    .filter(n => !n.children || n.children.length);
  const uc = UC[unit?.color] || UC.blue;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" data-testid="main-app">
      <UnitSwitcher units={units} currentUnit={currentUnitId} setCurrentUnit={switchUnit} user={currentUser} />

      <div className="flex flex-1 overflow-hidden">
        <div className={`${sidebarOpen ? "w-52" : "w-14"} bg-white border-r border-gray-200 flex flex-col transition-all duration-200 shrink-0`}>
          <div className="border-b border-gray-100">
            <div className="p-3 flex items-center gap-2">
              <img src="/logo.jpg" alt="Logo" className="w-8 h-8 rounded-lg object-contain shrink-0" />
              {sidebarOpen && <div className="text-xs font-bold text-gray-700 leading-tight flex-1">{unit?.shortName || "Anmol"}<br />Maintenance</div>}
              {sidebarOpen && (
                <button onClick={() => setSidebarOpen(o => !o)} className="text-gray-400 hover:text-gray-600 text-sm shrink-0 px-1" data-testid="sidebar-toggle-btn" title="Collapse sidebar" aria-label="Collapse sidebar">
                  {"\u25C0"}
                </button>
              )}
            </div>
            {!sidebarOpen && (
              <button onClick={() => setSidebarOpen(o => !o)} className="w-full py-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 text-sm text-center border-t border-gray-100" data-testid="sidebar-toggle-btn" title="Expand sidebar" aria-label="Expand sidebar">
                {"\u25B6"}
              </button>
            )}
          </div>
          {sidebarOpen && (
            <div className="px-3 py-2 border-b border-gray-100">
              <div className="text-xs text-gray-500 truncate font-medium">{currentUser.name}</div>
              <RoleBadge role={currentUser.role} />
            </div>
          )}
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
            {visibleNav.map(n => {
              if (!n.children) {
                return (
                  <button key={n.id} onClick={() => navFromMenu(n.id)}
                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-bold transition-colors ${view === n.id ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"}`}
                    data-testid={`nav-${n.id}`}>
                    {sidebarOpen && <span>{n.label}</span>}
                  </button>
                );
              }
              const holdsCurrent = GROUP_OF_VIEW[view] === n.id;
              // While the sidebar is collapsed there are no labels to nest under, so the
              // children are shown flat rather than hidden behind a heading nobody can read.
              const expanded = !sidebarOpen || holdsCurrent || !!openGroups[n.id];
              return (
                <div key={n.id}>
                  {sidebarOpen && (
                    <button onClick={() => setOpenGroups(g => ({ ...g, [n.id]: !expanded }))}
                      aria-expanded={expanded}
                      className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded-lg text-sm font-bold transition-colors ${holdsCurrent ? "text-blue-700" : "text-gray-700"} hover:bg-gray-100`}
                      data-testid={`nav-group-${n.id}`}>
                      <span>{n.label}</span>
                      <span className={`text-sm font-bold leading-none transition-transform ${expanded ? "rotate-90" : ""} ${holdsCurrent ? "text-blue-600" : "text-gray-500"}`} aria-hidden="true">&#9656;</span>
                    </button>
                  )}
                  {expanded && (
                    <div className={sidebarOpen ? "ml-2 pl-2 border-l border-gray-200 space-y-1 mt-1" : "space-y-1"}>
                      {n.children.map(c => (
                        <button key={c.id} onClick={() => navFromMenu(c.id)}
                          className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium transition-colors ${view === c.id || (c.id === "machines" && view === "history") ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                          data-testid={`nav-${c.id}`}>
                          {sidebarOpen && <span>{c.label}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between sticky top-0 z-10">
            <div className="text-sm font-semibold text-gray-700">{unit?.name}</div>
            <div className="flex items-center gap-3">
              {/* Name and role live in the sidebar while it is open; repeat them here
                  only when it is collapsed and they would otherwise be hidden. */}
              {!sidebarOpen && <span className="text-xs text-gray-500">{currentUser.name}</span>}
              {!sidebarOpen && <RoleBadge role={currentUser.role} />}
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-200"
                data-testid="logout-btn"
                title="Sign out"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" x2="9" y1="12" y2="12" />
                </svg>
                Sign Out
              </button>
            </div>
          </div>
          <div className="max-w-7xl mx-auto p-4 md:p-6">
            {view === "dashboard" && <Dashboard unitMachines={machines} unitPM={pmPlan} unitSpares={spareParts} historyLogs={historyLogs} unit={unit} user={currentUser} unitId={currentUnitId} onOpenHistory={(m, logId) => { setHighlightLogId(logId || null); navigate("history", m); }} />}
            {view === "machines" && <MachineListPage key={`ml-${currentUnitId}`} machines={machines} setMachines={setMachines} user={currentUser} onSelect={m => navigate("history", m)} unitId={currentUnitId} refreshMachines={refreshMachines} refreshPM={refreshPM} departments={departments} floors={floors} units={units} filters={machineFilters} onFiltersChange={setMachineFilters} pmCheckSheetTemplates={pmCheckSheetTemplates} pmPlan={pmPlan} />}
            {view === "instruments" && <InstrumentListPage key={`il-${currentUnitId}`} instruments={instruments} user={currentUser} unitId={currentUnitId} refreshInstruments={refreshInstruments} departments={departments} units={units} />}
            {view === "calibration" && <CalibrationPlan key={`cp-${currentUnitId}`} user={currentUser} unitId={currentUnitId} />}
            {view === "pmchecksheets" && <PMCheckSheetsAdmin key={`pmcs-${currentUnitId}`} user={currentUser} unitId={currentUnitId} machines={machines} />}
            {view === "history" && selectedMachine && <HistoryCard autoResumeDraft={resumeDraftMachineId === selectedMachine.id} onAutoResumed={() => setResumeDraftMachineId(null)} machine={selectedMachine} historyLogs={historyLogs} pmPlan={pmPlan} onBack={() => navigate("machines")} onGoToPM={() => navigate("pm")} user={currentUser} unitId={currentUnitId} refreshHistory={refreshHistory} spareParts={spareParts} highlightLogId={highlightLogId} onClearHighlight={() => setHighlightLogId(null)} />}
            {view === "pm" && <PMPlan key={`pm-${currentUnitId}-${pmSearchSeed}`} initialSearch={pmSearchSeed} pmPlan={pmPlan} setPmPlan={setPmPlan} user={currentUser} unitId={currentUnitId} refreshPM={refreshPM} machines={machines} floors={floors} pmCheckSheetTemplates={pmCheckSheetTemplates} />}
            {view === "spare" && <SpareParts key={`sp-${currentUnitId}`} spareParts={spareParts} setSpareParts={setSpareParts} user={currentUser} unitId={currentUnitId} refreshSpares={refreshSpares} />}
            {view === "drafts" && <DraftsPage key={`dr-${currentUnitId}`} user={currentUser} unitId={currentUnitId} units={units} machines={machines} onOpenMachine={m => { setResumeDraftMachineId(m.id); navigate("history", m); }} />}
            {view === "checksheet" && <Checksheets checksheets={checksheets} setChecksheets={setChecksheets} user={currentUser} />}
            {view === "reports" && <Reports key={`rp-${currentUnitId}`} user={currentUser} units={units} currentUnitId={currentUnitId} onFixPMHealth={fixPMHealth} />}
            {view === "settings" && <Settings users={users} setUsers={setUsers} units={units} setUnits={setUnits} user={currentUser} refreshUsers={fetchUsers} refreshUnits={refreshUnits} departments={departments} refreshDepartments={refreshDepartments} floors={floors} refreshFloors={refreshFloors} refreshMachines={refreshMachines} refreshPM={refreshPM} unitId={currentUnitId} machines={machines} />}
          </div>
        </div>
      </div>
    </div>
  );
}
